import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Sparkles, ExternalLink, X, ChevronRight, UserCheck, HelpCircle, Users } from "lucide-react";
import { MBTI_GROUPS } from "@/lib/mbti";

// LocalStorage flag: lets users postpone the wizard until next session.
const SKIP_FLAG = "kwgl_onboarding_skipped_until";

function isSkippedNow() {
  try {
    const ts = parseInt(localStorage.getItem(SKIP_FLAG) || "0", 10);
    return ts && Date.now() < ts;
  } catch (_e) { /* private/incognito mode — localStorage blocked */ return false; }
}

function skipForOneDay() {
  try {
    localStorage.setItem(SKIP_FLAG, String(Date.now() + 24 * 3600 * 1000));
  } catch (e) { /* private/incognito mode — localStorage blocked */ console.debug("skipForOneDay storage blocked:", e?.message); }
}

/**
 * One-time self-service wizard. Triggered on Dashboard load when the player still has
 * MBTI or recruited_by unset. Skippable via "Lembrar-me amanhã".
 *
 * Two steps:
 *   1. MBTI choice — if not yet done, lets the player pick their 16Personalities type.
 *      If they haven't taken the test, links them to https://www.16personalities.com/.
 *   2. Recruiter — pick from a list of existing players (or "Inscrevi-me sozinho").
 *      Auto-credits +20 GS_internal to the chosen recruiter on first reporting.
 *
 * The backend rejects re-submissions (one-shot fields), so this is safe even if the
 * player closes & re-opens the modal across sessions.
 */
export default function OnboardingWizard({ onComplete }) {
  const [data, setData] = useState(null);
  const [step, setStep] = useState(null); // 'mbti' | 'recruiter' | 'team' | 'done'
  const [mbtiChoice, setMbtiChoice] = useState("");
  const [tookTest, setTookTest] = useState(null); // null = not asked, true/false
  const [recruiterId, setRecruiterId] = useState("");
  const [recruiterSearch, setRecruiterSearch] = useState("");
  const [refRecruiter, setRefRecruiter] = useState(null); // pre-filled from ?ref= magic link
  const [teamId, setTeamId] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  // Detect ?ref=PLAYER_ID in the URL (magic invite link). The actual capture happens
  // in App.js at the root (so it survives the auth redirect for new joiners). Here we
  // only READ the sessionStorage value that was written earlier.
  useEffect(() => {
    /* no-op — kept for backward compat with previous tests that mocked window.location */
  }, []);

  useEffect(() => {
    if (isSkippedNow()) return;
    api.get("/my-profile/onboarding/options").then((r) => {
      setData(r.data);
      if (r.data?.needed && !r.data?.no_player) {
        // Pre-fetch recruiter info if user landed via magic link
        let pendingRef = null;
        try { pendingRef = sessionStorage.getItem("kwgl_invite_ref"); } catch (e) { console.debug("sessionStorage blocked:", e?.message); }
        if (pendingRef && r.data.needs_recruiter) {
          api.get(`/invites/preview/${pendingRef}`).then((p) => {
            setRefRecruiter(p.data);
            setRecruiterId(p.data.id);
          }).catch((e) => { console.debug("invite preview failed (invalid ref):", e?.message); });
        }
        setStep(r.data.needs_mbti ? "mbti" : (r.data.needs_recruiter ? "recruiter" : "team"));
        setOpen(true);
      }
    }).catch(() => {});
  }, []);

  if (!open || !data?.needed) return null;

  // For coaches without MBTI, the modal is BLOCKING — cannot be dismissed (no X, no "remind later").
  // Rationale: identifying their own MBTI is a prerequisite for the coaching/educational role.
  const isCoachMandatory = data.is_coach && data.needs_mbti;
  const close = () => { setOpen(false); onComplete && onComplete(); };
  const remindLater = () => { skipForOneDay(); close(); toast.info("Vamos lembrar-te amanhã."); };

  const submitMbti = async () => {
    if (!mbtiChoice) { toast.error("Escolhe o teu tipo MBTI antes de continuar."); return; }
    setSaving(true);
    try {
      await api.put("/my-profile/onboarding", { mbti: mbtiChoice });
      toast.success(`MBTI definido: ${mbtiChoice}`);
      if (data.needs_recruiter) { setStep("recruiter"); }
      else if (data.needs_team) { setStep("team"); }
      else { setStep("done"); }
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const submitRecruiter = async (skip) => {
    setSaving(true);
    try {
      const payload = skip ? { skip_recruiter: true } : { recruited_by: recruiterId };
      await api.put("/my-profile/onboarding", payload);
      try { sessionStorage.removeItem("kwgl_invite_ref"); } catch (e) { console.debug("sessionStorage cleanup blocked:", e?.message); }
      toast.success(skip ? "Registado: inscreveste-te sozinho." : "Recrutador atribuído. +20 pts vão para o teu padrinho/madrinha quando reportares pela primeira vez.");
      if (data.needs_team) { setStep("team"); }
      else { setStep("done"); }
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const submitTeam = async (skip) => {
    setSaving(true);
    try {
      const payload = skip ? { skip_team: true } : { team_id: teamId };
      await api.put("/my-profile/onboarding", payload);
      toast.success(skip ? "Sem equipa, sem problema. Bem-vindo à liga!" : "Equipa atribuída.");
      setStep("done");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const filteredRecruiters = (data.recruiters || []).filter((r) =>
    !recruiterSearch || (r.name || "").toLowerCase().includes(recruiterSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="kw-card w-full max-w-2xl flex flex-col" style={{ background: "#0A0A0A", maxHeight: "calc(100vh - 2rem)" }}>
        {/* Header (sticky) */}
        <div className="flex items-center justify-between border-b border-[#222] p-5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#E50000]" />
            <div>
              <div className="kw-overline text-[#E50000]">Boas-vindas à KW Lead Growth League</div>
              <h2 className="font-display text-xl md:text-2xl font-black uppercase">Vamos completar o teu perfil</h2>
            </div>
          </div>
          <button
            onClick={close}
            disabled={isCoachMandatory}
            className={`text-[#737373] hover:text-white ${isCoachMandatory ? "opacity-30 cursor-not-allowed" : ""}`}
            data-testid="onboarding-close"
            title={isCoachMandatory ? "Como coach, é obrigatório definires o teu MBTI antes de prosseguir." : "Fechar (volta a aparecer mais tarde)"}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress dots (sticky) */}
        <div className="flex items-center gap-2 px-5 pt-4 pb-2 flex-wrap flex-shrink-0 border-b border-[#1A1A1A]">
          <Dot active={step === "mbti"} done={step === "recruiter" || step === "team" || step === "done" || !data.needs_mbti} label="MBTI" />
          {data.needs_recruiter && (
            <>
              <ChevronRight className="w-3 h-3 text-[#444]" />
              <Dot active={step === "recruiter"} done={step === "team" || step === "done"} label="Quem te trouxe" />
            </>
          )}
          {data.needs_team && (
            <>
              <ChevronRight className="w-3 h-3 text-[#444]" />
              <Dot active={step === "team"} done={step === "done"} label="Equipa" />
            </>
          )}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {step === "mbti" && (
            <div className="space-y-5" data-testid="onboarding-step-mbti">
              <div className="border-l-2 border-[#E50000] pl-3 text-xs text-[#A3A3A3] leading-relaxed"
                data-testid="onboarding-glossary">
                <strong className="text-white">Coopetir</strong> é competir e cooperar ao mesmo tempo: medimo-nos
                uns com os outros para subir o nível e partilhamos o que funciona.
                Reportas a tua semana e os pontos contam para ti e para o teu clube MBTI.
                Ganha o clube que crescer mais em conjunto — nunca à custa de ninguém.
              </div>
              <p className="text-sm text-[#A3A3A3]">
                {data.is_coach ? (
                  <>O teu <strong className="text-white">MBTI pessoal</strong> não te liga ao clube que coordenas — o clube é atribuído pelo Presidente da Liga. Mas como coach é importante conheceres o teu perfil para te conectares com o teu próprio padrão de comunicação e estilo.</>
                ) : (
                  <>A liga organiza-se em <strong className="text-white">4 clubes</strong> baseados no perfil 16Personalities (MBTI). Diz-nos o teu para entrares no clube certo.</>
                )}
              </p>

              {isCoachMandatory && (
                <div className="border border-[#E50000]/50 bg-[#E50000]/10 p-3 text-xs text-[#FCA5A5]" data-testid="onboarding-coach-mandatory">
                  <strong className="text-white uppercase tracking-widest">Obrigatório:</strong> como coach,
                  precisas de identificar o teu perfil MBTI para continuar a usar a plataforma. Este passo não pode ser adiado.
                </div>
              )}

              {tookTest === null && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <button onClick={() => setTookTest(true)} className="border border-[#222] hover:border-[#E50000] p-4 text-left transition-colors" data-testid="onboarding-test-yes">
                    <UserCheck className="w-5 h-5 text-[#E50000] mb-2" />
                    <div className="font-display font-black uppercase tracking-tight">Já fiz o teste</div>
                    <div className="text-xs text-[#A3A3A3] mt-1">Escolhe o teu tipo da lista abaixo.</div>
                  </button>
                  <button onClick={() => setTookTest(false)} className="border border-[#222] hover:border-[#3B82F6] p-4 text-left transition-colors" data-testid="onboarding-test-no">
                    <HelpCircle className="w-5 h-5 text-[#3B82F6] mb-2" />
                    <div className="font-display font-black uppercase tracking-tight">Ainda não fiz</div>
                    <div className="text-xs text-[#A3A3A3] mt-1">Faz agora em ~12 min (gratuito).</div>
                  </button>
                </div>
              )}

              {tookTest === false && (
                <div className="space-y-3 border border-[#3B82F6]/40 bg-[#3B82F6]/5 p-4">
                  <p className="text-sm">Faz o teste oficial agora:</p>
                  <a href="https://www.16personalities.com/" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 kw-btn-primary !py-2" data-testid="onboarding-test-link"
                    onClick={() => { skipForOneDay(); }}>
                    <ExternalLink className="w-4 h-4" /> Abrir 16personalities.com
                  </a>
                  <p className="text-xs text-[#737373]">Depois de teres o resultado, volta e escolhe o teu tipo na lista. Lembramos-te amanhã.</p>
                  <button onClick={() => setTookTest(true)} className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white">
                    Já tenho o resultado →
                  </button>
                </div>
              )}

              {tookTest === true && (
                <div className="space-y-4" data-testid="onboarding-mbti-picker">
                  {MBTI_GROUPS.map((group) => (
                    <div key={group.club}>
                      <div className="kw-overline mb-2" style={{ color: group.color }}>{group.club}</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {group.types.map((t) => (
                          <button key={t.code}
                            onClick={() => setMbtiChoice(t.code)}
                            className={`border p-3 text-left transition-colors ${
                              mbtiChoice === t.code
                                ? "border-[#E50000] bg-[#E50000]/10"
                                : "border-[#222] hover:border-[#444]"
                            }`}
                            data-testid={`onboarding-mbti-${t.code}`}>
                            <div className="font-display text-lg font-black">{t.code}</div>
                            <div className="text-[10px] text-[#A3A3A3] uppercase tracking-widest">{t.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end gap-2 pt-2 border-t border-[#222]">
                    {!isCoachMandatory && (
                      <button onClick={remindLater} className="kw-btn-secondary !py-2 text-xs" data-testid="onboarding-mbti-later">Lembrar amanhã</button>
                    )}
                    <button onClick={submitMbti} disabled={saving || !mbtiChoice} className="kw-btn-primary !py-2 text-xs" data-testid="onboarding-mbti-confirm">
                      {saving ? "A guardar…" : "Confirmar MBTI →"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === "recruiter" && (
            <div className="space-y-4" data-testid="onboarding-step-recruiter">
              {refRecruiter ? (
                <div className="space-y-4" data-testid="onboarding-magic-link-card">
                  <div className="border border-[#E50000]/40 bg-[#E50000]/5 p-5 flex items-center gap-4">
                    {refRecruiter.photo_url ? (
                      <img src={refRecruiter.photo_url} alt={refRecruiter.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#E50000]" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#E50000]/20 flex items-center justify-center font-display text-xl font-black text-[#E50000]">
                        {(refRecruiter.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="kw-overline text-[#E50000]">Foste convidado por</div>
                      <div className="font-display text-2xl font-black uppercase tracking-tight">{refRecruiter.name}</div>
                      <div className="text-xs text-[#A3A3A3] mt-0.5">
                        {refRecruiter.club || "—"} {refRecruiter.mbti ? `· ${refRecruiter.mbti}` : ""} {refRecruiter.personality_label ? `· ${refRecruiter.personality_label}` : ""}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-[#A3A3A3]">
                    Ao confirmar, <strong className="text-white">{refRecruiter.name}</strong> recebe <strong className="text-white">+20 pts</strong> quando fizeres o primeiro reporting. Se este não é o teu recrutador, podes <button onClick={() => setRefRecruiter(null)} className="underline text-[#E50000]" data-testid="onboarding-refuse-magic-link">escolher outro manualmente</button>.
                  </p>
                  <div className="flex flex-wrap justify-between gap-2 pt-2 border-t border-[#222]">
                    <button onClick={() => submitRecruiter(true)} disabled={saving}
                      className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white" data-testid="onboarding-recruiter-alone">
                      Inscrevi-me sozinho →
                    </button>
                    <div className="flex gap-2">
                      <button onClick={remindLater} className="kw-btn-secondary !py-2 text-xs" data-testid="onboarding-recruiter-later">Lembrar amanhã</button>
                      <button onClick={() => submitRecruiter(false)} disabled={saving} className="kw-btn-primary !py-2 text-xs" data-testid="onboarding-recruiter-confirm-magic">
                        {saving ? "A guardar…" : `Confirmar ${refRecruiter.name.split(" ")[0]} →`}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-[#A3A3A3]">
                    Quem te recomendou para a liga? O teu padrinho/madrinha ganha <strong className="text-white">+20 pts</strong> quando fizeres o primeiro reporting.
                  </p>
                  <input
                    type="text"
                    className="kw-input w-full"
                    placeholder="Procurar pelo nome…"
                    value={recruiterSearch}
                    onChange={(e) => setRecruiterSearch(e.target.value)}
                    data-testid="onboarding-recruiter-search"
                  />
                  <div className="max-h-72 overflow-y-auto border border-[#222]">
                    {filteredRecruiters.length === 0 && (
                      <p className="text-sm text-[#737373] p-4 text-center italic">Sem resultados.</p>
                    )}
                    {filteredRecruiters.map((r) => (
                      <button key={r.id}
                        onClick={() => setRecruiterId(r.id)}
                        className={`w-full text-left p-3 border-b border-[#1A1A1A] flex items-center justify-between transition-colors ${
                          recruiterId === r.id ? "bg-[#E50000]/15" : "hover:bg-[#141414]"
                        }`}
                        data-testid={`onboarding-recruiter-${r.id}`}>
                        <div>
                          <div className="font-semibold">{r.name}</div>
                          <div className="text-[10px] text-[#737373] uppercase tracking-widest">{r.club || "—"} {r.mbti ? `· ${r.mbti}` : ""}</div>
                        </div>
                        {recruiterId === r.id && <UserCheck className="w-4 h-4 text-[#E50000]" />}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap justify-between gap-2 pt-2 border-t border-[#222]">
                    <button onClick={() => submitRecruiter(true)} disabled={saving}
                      className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white" data-testid="onboarding-recruiter-alone">
                      Inscrevi-me sozinho →
                    </button>
                    <div className="flex gap-2">
                      <button onClick={remindLater} className="kw-btn-secondary !py-2 text-xs" data-testid="onboarding-recruiter-later">Lembrar amanhã</button>
                      <button onClick={() => submitRecruiter(false)} disabled={saving || !recruiterId} className="kw-btn-primary !py-2 text-xs" data-testid="onboarding-recruiter-confirm">
                        {saving ? "A guardar…" : "Confirmar recrutador →"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {step === "team" && (
            <div className="space-y-4" data-testid="onboarding-step-team">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#E50000]" />
                <p className="text-sm text-[#A3A3A3]">
                  Pertences a alguma <strong className="text-white">equipa</strong> dentro da KW Lead? Escolhe a tua — ou indica que ainda não pertences a nenhuma.
                </p>
              </div>
              {(data.teams || []).length === 0 ? (
                <p className="text-sm text-[#737373] italic border border-[#222] p-4 text-center">
                  Ainda não há equipas configuradas. Podes saltar este passo.
                </p>
              ) : (
                <div className="max-h-72 overflow-y-auto border border-[#222]">
                  {(data.teams || []).map((t) => (
                    <button key={t.id}
                      onClick={() => setTeamId(t.id)}
                      className={`w-full text-left p-3 border-b border-[#1A1A1A] flex items-center justify-between transition-colors ${
                        teamId === t.id ? "bg-[#E50000]/15" : "hover:bg-[#141414]"
                      }`}
                      data-testid={`onboarding-team-${t.id}`}>
                      <div>
                        <div className="font-semibold">{t.name}</div>
                        <div className="text-[10px] text-[#737373] uppercase tracking-widest">
                          {t.members_count} {t.members_count === 1 ? "membro" : "membros"}
                        </div>
                      </div>
                      {teamId === t.id && <UserCheck className="w-4 h-4 text-[#E50000]" />}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap justify-between gap-2 pt-2 border-t border-[#222]">
                <button onClick={() => submitTeam(true)} disabled={saving}
                  className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white" data-testid="onboarding-team-none">
                  Não pertenço a nenhuma equipa →
                </button>
                <div className="flex gap-2">
                  <button onClick={remindLater} className="kw-btn-secondary !py-2 text-xs" data-testid="onboarding-team-later">Lembrar amanhã</button>
                  <button onClick={() => submitTeam(false)} disabled={saving || !teamId} className="kw-btn-primary !py-2 text-xs" data-testid="onboarding-team-confirm">
                    {saving ? "A guardar…" : "Confirmar equipa →"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="text-center py-6" data-testid="onboarding-done">
              <Sparkles className="w-12 h-12 text-[#E50000] mx-auto mb-3" />
              <h3 className="font-display text-2xl font-black uppercase">Perfil completo</h3>
              <p className="text-sm text-[#A3A3A3] mt-2">Bem-vindo à liga. Boa jornada!</p>
              <button onClick={close} className="kw-btn-primary mt-5" data-testid="onboarding-finish">
                Começar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Dot({ active, done, label }) {
  const color = done ? "#22C55E" : active ? "#E50000" : "#333";
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: active ? "#fff" : "#737373" }}>{label}</span>
    </div>
  );
}
